// Lets a buyer request their internship training program certificate for a
// paid purchase, and shows the request's status (pending / mailed) once
// submitted. Kept as its own component (rather than editing PurchaseDetailsPage.jsx
// deeply) since that page's source is a minified reconstruction.
import { React } from "../vendor/runtime.js";
import {
  AwardIcon,
  CheckIcon,
  CircleAlertIcon,
  ClockIcon,
  LoaderCircleIcon,
} from "../vendor/runtime.js";
import { useAuth } from "../context/AuthContext.jsx";
import { api } from "../api/client.js";

const CertificateRequestCard = ({ purchaseId }) => {
  const { currentUser } = useAuth();

  const [loading, setLoading] = React.useState(true);
  const [request, setRequest] = React.useState(null); // null = not yet requested
  const [showForm, setShowForm] = React.useState(false);
  const [fullName, setFullName] = React.useState(currentUser?.name || "");
  const [email, setEmail] = React.useState(currentUser?.email || "");
  const [dob, setDob] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await api.get(`/certificate-requests/purchase/${purchaseId}`);
        if (!cancelled) setRequest(res.data);
      } catch (err) {
        if (!cancelled && err.response?.status !== 404) {
          console.error("Error loading certificate request:", err);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [purchaseId]);

  const submitRequest = async (e) => {
    e.preventDefault();
    if (!fullName.trim() || !email.trim() || !dob || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await api.post("/certificate-requests", {
        purchaseId,
        fullName: fullName.trim(),
        email: email.trim(),
        dob,
      });
      setRequest({ id: res.data.id, status: "pending", created_at: new Date().toISOString() });
      setShowForm(false);
    } catch (err) {
      console.error("Error requesting certificate:", err);
      setError(err.response?.data?.error || "Couldn't submit your request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const cardShell = (children) => (
    <div
      className={
        "bg-gradient-to-br from-slate-50 to-indigo-50 rounded-xl sm:rounded-2xl p-6 sm:p-8 border border-slate-200"
      }
    >
      <h2 className={"text-lg sm:text-xl font-bold text-slate-800 mb-4 sm:mb-6 flex items-center"}>
        <span
          className={
            "w-5 h-5 sm:w-6 sm:h-6 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-lg flex items-center justify-center mr-2 sm:mr-3"
          }
        >
          <AwardIcon className={"w-3 h-3 sm:w-3.5 sm:h-3.5 text-white"} />
        </span>
        {"Internship Certificate"}
      </h2>
      {children}
    </div>
  );

  if (loading) {
    return cardShell(
      <div className={"flex items-center text-slate-500 text-sm"}>
        <LoaderCircleIcon className={"w-4 h-4 mr-2 animate-spin"} />
        {"Checking certificate status..."}
      </div>
    );
  }

  if (request?.status === "mailed") {
    return cardShell(
      <div className={"flex items-start"}>
        <div
          className={
            "w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-green-100 flex items-center justify-center mr-3 flex-shrink-0"
          }
        >
          <CheckIcon className={"w-4 h-4 sm:w-5 sm:h-5 text-green-600"} />
        </div>
        <div>
          <p className={"font-semibold text-slate-800 text-sm sm:text-base"}>
            {"Certificate emailed"}
          </p>
          <p className={"text-xs sm:text-sm text-slate-600 mt-1"}>
            {"Your internship training program certificate has been sent to your email. Please check your inbox (and spam folder)."}
          </p>
        </div>
      </div>
    );
  }

  if (request?.status === "pending") {
    return cardShell(
      <div className={"flex items-start"}>
        <div
          className={
            "w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-amber-100 flex items-center justify-center mr-3 flex-shrink-0"
          }
        >
          <ClockIcon className={"w-4 h-4 sm:w-5 sm:h-5 text-amber-600"} />
        </div>
        <div>
          <p className={"font-semibold text-slate-800 text-sm sm:text-base"}>
            {"Request submitted"}
          </p>
          <p className={"text-xs sm:text-sm text-slate-600 mt-1"}>
            {"Your certificate request is being processed. We'll email it to you shortly."}
          </p>
        </div>
      </div>
    );
  }

  return cardShell(
    <>
      <p className={"text-xs sm:text-sm text-slate-600 mb-4"}>
        {
          "This purchase comes with a free internship training program certificate. Fill in your details below and we'll email it to you."
        }
      </p>
      {!showForm ? (
        <button
          onClick={() => setShowForm(true)}
          className={
            "inline-flex items-center px-4 py-2.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors"
          }
        >
          <AwardIcon className={"w-4 h-4 mr-2"} />
          {"Request Certificate"}
        </button>
      ) : (
        <form onSubmit={submitRequest} className={"space-y-3 sm:space-y-4"}>
          <div>
            <label className={"block text-xs sm:text-sm font-medium text-slate-600 mb-1"}>
              {"Full Name"}
            </label>
            <input
              type={"text"}
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className={
                "w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              }
              required
            />
          </div>
          <div>
            <label className={"block text-xs sm:text-sm font-medium text-slate-600 mb-1"}>
              {"Email"}
            </label>
            <input
              type={"email"}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={
                "w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              }
              required
            />
          </div>
          <div>
            <label className={"block text-xs sm:text-sm font-medium text-slate-600 mb-1"}>
              {"Date of Birth"}
            </label>
            <input
              type={"date"}
              value={dob}
              onChange={(e) => setDob(e.target.value)}
              className={
                "w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              }
              required
            />
          </div>
          {error && (
            <p className={"flex items-start text-xs sm:text-sm text-red-600"}>
              <CircleAlertIcon className={"w-4 h-4 mr-1.5 flex-shrink-0 mt-0.5"} />
              {error}
            </p>
          )}
          <div className={"flex gap-2"}>
            <button
              type={"submit"}
              disabled={submitting}
              className={
                "inline-flex items-center px-4 py-2.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:bg-indigo-300 disabled:cursor-not-allowed transition-colors"
              }
            >
              {submitting ? (
                <LoaderCircleIcon className={"w-4 h-4 animate-spin"} />
              ) : (
                "Submit Request"
              )}
            </button>
            <button
              type={"button"}
              onClick={() => setShowForm(false)}
              className={
                "px-4 py-2.5 text-slate-600 text-sm font-medium rounded-lg hover:bg-slate-100 transition-colors"
              }
            >
              {"Cancel"}
            </button>
          </div>
        </form>
      )}
    </>
  );
};

export { CertificateRequestCard };
